var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { ref } from 'valtio';
import { getInternalValidator, } from './schema';
function hasFocus(element) {
    return 'focus' in element;
}
function hasSetFocus(element) {
    return 'setFocus' in element;
}
// this is the internal API of Field; users of `useForm` cannot
// access anything on this.
export class FieldImpl {
    constructor({ parent, name, value, error, visible, componentSchema, validator, updater, updateCallback, validation, }) {
        // we hold a react Ref in here. We exclude it from valtio with its entirely
        // distinct 'ref' declaration.
        this.ref = ref(React.createRef());
        this.parent = parent;
        this.name = name;
        this.value = value;
        this.error = error;
        this.visible = visible;
        this.componentSchema = componentSchema;
        this.validator = validator;
        this.updater = updater;
        this.updateCallback = updateCallback;
        this.validation = validation;
    }
    setComponentSchema(componentSchema) {
        this.componentSchema = componentSchema;
        this.validator = getInternalValidator(this.validation, componentSchema);
    }
    setValue(value, seen = new Set()) {
        seen.add(this.name);
        this.value = value;
        const change = (name, value) => {
            // avoid infinite updates
            if (seen.has(name)) {
                return;
            }
            this.parent[name].setValue(value, seen);
        };
        this.updater(value, change);
        this.updateCallback({ name: this.name, value: value, fields: this.parent, change });
    }
    setVisible(status) {
        this.visible = status;
    }
    // to help making fieldsCopy work in the tests, avoiding
    // circular reference
    toJSON() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _a = this, { parent, ref } = _a, result = __rest(_a, ["parent", "ref"]);
        return result;
    }
    focus() {
        const element = this.ref.current;
        if (element == null) {
            return;
        }
        if (hasFocus(element)) {
            element.focus();
        }
        else if (hasSetFocus(element)) {
            element.setFocus();
        }
    }
}
export const stateToFields = (state, schema) => {
    const result = {};
    names(state).forEach((name) => {
        result[name] = new FieldImpl(Object.assign({ parent: result, name: name, componentSchema: null }, fieldArgs(state, schema, name)));
    });
    return result;
};
const fieldArgs = (state, schema, name) => {
    const validation = schema[name];
    const startsVisible = validation === null || validation === void 0 ? void 0 : validation.startsVisible;
    return {
        value: state[name],
        error: '',
        // eslint-disable-next-line deprecation/deprecation
        updater: (validation === null || validation === void 0 ? void 0 : validation.update) || (() => { }),
        updateCallback: (validation === null || validation === void 0 ? void 0 : validation.updateCallback) || (() => { }),
        visible: startsVisible != null ? startsVisible : true,
        validator: getInternalValidator(validation, null),
        validation,
    };
};
export const update = (fields, state) => {
    names(state).forEach((name) => {
        fields[name].value = state[name];
    });
};
export const setComponentSchema = (fields, name, componentSchema) => {
    fields[name].setComponentSchema(componentSchema);
};
export const fieldsToState = (fields) => {
    const result = {};
    names(fields).forEach((name) => {
        result[name] = fields[name].value;
    });
    return result;
};
export const validate = (fields) => {
    names(fields).forEach((name) => {
        const field = fields[name];
        if (!field.visible) {
            field.error = '';
            return;
        }
        field.error = field.validator(field.value) || '';
    });
};
export const getFirstInvalidFieldName = (fields) => {
    return names(fields).find((name) => fields[name].error);
};
const names = (o) => {
    return Object.keys(o);
};
